/**
 * sl-hle4.8: Custom Apps Settings Tab
 *
 * Tab shell for the workspace's "Custom Apps" surface. Wraps the
 * domain/host management section. Future sections (sl-hle4.9 Bandwidth,
 * sl-hle4.11 SEO/SERP, sl-hle4.12 AIEO, sl-hle4.13 Multi-Locale,
 * sl-hle4.14 Custom Components) plug in here as siblings of
 * `<CustomDomainSection />`.
 *
 * Pattern follows the AI / Automations consolidated tabs:
 *   `SettingHeader` for tab title + subtitle, then sections rendered
 *   inline (each section owns its own data fetching and plan gating).
 *
 * Plan-gate visibility for the entry in the sidebar is enforced in
 * `workspace-setting/index.tsx` via `FEATURE_KEY_CUSTOM_APPS_CREATE`.
 *
 * SOLID:
 *   - S: tab-shell only — no business logic, sections are independent.
 *   - O: new sections are added by importing them next to CustomDomainSection.
 */
import { Button } from '@affine/component';
import { SettingHeader } from '@affine/component/setting-components';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { FolderShareModal } from '@affine/core/modules/folder-share';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useWorkspaceCustomApps } from '@affine/core/modules/streamlain/hooks/use-workspace-custom-apps';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import type { SettingState } from '../../types';
import {
  SettingsActionRow,
  SettingsContentSection,
  SettingsContentText,
} from '../shared/settings-content';
import { SettingsEditorScopeHint } from '../shared/settings-editor-scope-hint';
import { AieoSection } from './aieo-section';
import { BandwidthSection } from './bandwidth-section';
import { ComponentsSection } from './components-section';
import { CustomDomainSection } from './custom-domain-section';
import { LocaleSection } from './locale-section';
import { SeoSection } from './seo-section';

const APP_AREA_ROOT_SYSTEM_KIND = 'app_area_root';

const AppAreaAccessSection = () => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);
  const { isFeatureAllowed } = useFeatureGate();
  const hasCustomApps = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(accessibleFolderCache.watchAllAccessibleFolders(), []),
      [accessibleFolderCache]
    )
  );
  const appAreaRoot = useMemo(
    () =>
      accessibleFolders.find(
        folder =>
          folder.systemKind === APP_AREA_ROOT_SYSTEM_KIND &&
          folder.parentId === null
      ) ?? null,
    [accessibleFolders]
  );

  const { views, isValidating: customAppsValidating } = useWorkspaceCustomApps(
    isAdmin && hasCustomApps ? workspaceService.workspace.id : null
  );

  useEffect(() => {
    if (!isAdmin || !hasCustomApps) {
      return;
    }
    accessibleFolderCache.fetchAccessibleFolders().catch(error => {
      console.error('Failed to load app area root folder:', error);
    });
  }, [
    accessibleFolderCache,
    customAppsValidating,
    hasCustomApps,
    isAdmin,
    views?.length,
  ]);

  if (!isAdmin || !hasCustomApps) {
    return null;
  }

  return (
    <SettingsContentSection
      title={t['com.streamlain.settings.workspace.customApps.appArea.title']()}
    >
      <SettingsContentText>
        {t[
          'com.streamlain.settings.workspace.customApps.appArea.description'
        ]()}
      </SettingsContentText>
      <SettingsActionRow>
        <Button
          variant="secondary"
          disabled={!appAreaRoot}
          onClick={() => setShareModalOpen(true)}
          data-testid="workspace-custom-apps-app-area-access"
        >
          {t[
            'com.streamlain.settings.workspace.customApps.appArea.configure'
          ]()}
        </Button>
      </SettingsActionRow>
      {shareModalOpen && appAreaRoot ? (
        <FolderShareModal
          folderId={appAreaRoot.id}
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      ) : null}
    </SettingsContentSection>
  );
};

export const WorkspaceSettingCustomApps = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isEditor = information?.role === Permission.Editor;

  const title = t['com.streamlain.settings.workspace.customApps.title']();
  const subtitle = t['com.streamlain.settings.workspace.customApps.subtitle']();

  return (
    <>
      <SettingHeader title={title} subtitle={subtitle} />
      <AffineErrorBoundary>
        <AppAreaAccessSection />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <CustomDomainSection onChangeSettingState={onChangeSettingState} />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <BandwidthSection />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <SeoSection />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <AieoSection />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <LocaleSection />
      </AffineErrorBoundary>
      <AffineErrorBoundary>
        <ComponentsSection />
      </AffineErrorBoundary>
      {isEditor ? <SettingsEditorScopeHint /> : null}
    </>
  );
};
